import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "axis-lang";

type Lang = "ka" | "en";

// Each locale is its own chunk, fetched only when that language is in use.
// Both files together are ~340 KB raw; bundling them into the entry made every
// visitor download the language they are not reading. The two files carry the
// same keys, so `fallbackLng` never needs the other bundle to be present.
const LOADERS: Record<Lang, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import("./locales/en.json"),
  ka: () => import("./locales/ka.json"),
};

/**
 * Make sure `lang`'s translations are registered. Resolves at once when they
 * already are: after the first load, or in tests, where vitest.setup.ts
 * registers both bundles up front.
 */
export async function loadLanguage(lang: Lang): Promise<void> {
  if (i18n.hasResourceBundle(lang, "translation")) return;
  const mod = await LOADERS[lang]();
  i18n.addResourceBundle(lang, "translation", mod.default, true, true);
}

function detectInitialLang(): Lang {
  // `window.localStorage` can be undefined outside a real browser (e.g. the
  // vitest/node test env), so guard it rather than assume it exists.
  if (typeof window === "undefined" || !window.localStorage) return "ka";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "ka" || saved === "en") return saved;
  return "ka";
}

const initialLang = detectInitialLang();

/**
 * Resolves once i18next is initialised and the active language's bundle is
 * loaded. main.tsx waits for it before the first render, so no screen ever
 * paints raw translation keys.
 */
export const i18nReady: Promise<void> = i18n
  .use(initReactI18next)
  .init({
    // Bundles are added by `loadLanguage`; there is no i18next backend.
    resources: {},
    partialBundledLanguages: true,
    lng: initialLang,
    fallbackLng: "ka",
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  })
  .then(() => loadLanguage(initialLang));

export function setLanguage(lang: Lang): void {
  // Switch only once the bundle is in: switching first would render the whole
  // app as raw keys for the length of the fetch.
  void loadLanguage(lang).then(() => i18n.changeLanguage(lang));
  if (typeof window === "undefined") return;
  // `detectInitialLang` already guards a missing localStorage; writing needs
  // the same guard, and a try around the write itself. A browser can have the
  // object and still throw on set — private mode, blocked storage, quota. That
  // must not take the click handler down with it: remembering the choice is a
  // convenience, switching the language is the actual job.
  try {
    window.localStorage?.setItem(STORAGE_KEY, lang);
  } catch {
    // Not remembered for next time. Nothing else to do about it.
  }
  // Update <html lang="..."> for a11y + browser hints.
  document.documentElement.lang = lang;
}

// Set the initial <html lang> attribute on first import.
if (typeof document !== "undefined") {
  document.documentElement.lang = initialLang;
}

export default i18n;
